import React from 'react'
import {
  getApiBase,
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  authFetch,
} from '../lib/api'
import {
  AuthContext,
  type AuthUser,
  type AuthContextValue,
} from './auth-context'

const API = getApiBase()

export function AuthProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const [user, setUser] = React.useState<AuthUser | null>(null)
  const [loading, setLoading] = React.useState(true)

  const refreshUser = React.useCallback(async () => {
    const token = getAuthToken()
    if (!token) {
      setUser(null)
      setLoading(false)
      return
    }
    try {
      const res = await authFetch(`${API}/api/auth/me`)
      if (res.ok) {
        const data = await res.json()
        setUser(data)
      } else {
        clearAuthToken()
        setUser(null)
      }
    } catch {
      clearAuthToken()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    refreshUser()
  }, [refreshUser])

  const login = React.useCallback(async (email: string, password: string) => {
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        return {
          ok: false,
          error: (data as { error?: string }).error ?? 'Login failed',
        }
      }
      const token = (data as { token?: string }).token
      const userData = (data as { user?: AuthUser }).user
      if (token && userData) {
        setAuthToken(token)
        setUser(userData)
        return { ok: true }
      }
      return { ok: false, error: 'Invalid response' }
    } catch {
      return { ok: false, error: 'Network error' }
    }
  }, [])

  const logout = React.useCallback(() => {
    clearAuthToken()
    setUser(null)
  }, [])

  const value: AuthContextValue = React.useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      refreshUser,
    }),
    [user, loading, login, logout, refreshUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
